import { forwardRef, useMemo } from 'react'
import { observer } from 'mobx-react'
import {
  Block,
  Checkbox,
  CheckboxGroup,
  ExternalLink,
  Heading,
  Paragraph,
  RadioButton,
  RadioButtonGroup,
  SingleSelect,
  StatusText,
  Text,
} from 'suomifi-ui-components'
import type { StepComponent } from 'todistukset-ui/src/vtj/components/order-wizard/OrderWizardStep.type'
import { FormContainer } from 'todistukset-ui/src/vtj/components/order-wizard/CommonFormComponents.sc'
import LocalizedString from 'todistukset-ui/src/vtj/components/localized-string/LocalizedString.react'
import {
  getErrorStatus,
  useFirstErrorImperativeHandle,
} from 'todistukset-ui/src/vtj/components/order-wizard/errors.util'
import { useUaetForm } from 'todistukset-ui/src/vtj/stores/uaet-form.store'
import { UaetUiForm } from 'todistukset-common/src/vtj/types/form-types.type'
import {
  validateTranslationFormLanguage,
  validateUaetTodistusLanguage,
} from 'todistukset-common/src/vtj/uaet-validation'
import { finalEditableFieldValue } from 'todistukset-common/src/vtj/types/editable-field.type'
import { useUaetFormActions } from 'todistukset-ui/src/vtj/stores/actions/uaet-form-actions'
import { availableUAETTodistusLanguages } from 'todistukset-common/src/vtj/language.util'
import translateLanguage from 'common-translations/src/vtj/translate-language'
import { useStore } from 'todistukset-ui/src/vtj/stores/store.util'
import CertificateTypes from 'todistukset-common/src/vtj/CertificateTypes.enum'
import { useTranslation } from 'react-i18next'
import {
  isCountryInApostilleByCode,
  isCountryInEUByCode,
} from 'todistukset-common/src/vtj/country.util'
import { isTranslationFormAvailableUaet } from 'todistukset-common/src/vtj/form-check.util'
import {
  getSingleSelectLanguageDataMapper,
  getTranslationFormLanguageSingleSelectData,
} from 'todistukset-common/src/vtj/ui/translation-form-language-select.util'

const UaetAdditionalSelectionStep: StepComponent = observer(
  forwardRef(function UaetAdditionalSelectionStep({ titleId }, ref) {
    const form = useUaetForm()

    const refs = useFirstErrorImperativeHandle(ref, form, getErrors, [
      'todistusLanguage',
      'translationFormLanguage',
    ])

    return (
      <FormContainer>
        <Block variant="section">
          <Heading variant="h2" id={titleId}>
            <LocalizedString stringKey="orderWizardAdditionalSelectionsStepMainTitle" />
          </Heading>
        </Block>
        <Block variant="section">
          <TodistusLanguageSelection ref={refs.todistusLanguage} />
        </Block>
        {isTranslationFormAvailableUaet({
          todistusLanguage: form.todistusLanguage,
          placeOfMarriage: form.placeOfMarriage,
        }) && (
          <Block variant="section" data-test-id="translation-section">
            <TranslationFormSelections ref={refs.translationFormLanguage} />
          </Block>
        )}
        {isCountryInApostilleByCode(
          finalEditableFieldValue(form.placeOfMarriage)
        ) && (
          <Block variant="section" data-test-id="apostille-section">
            <ApostilleSelection />
          </Block>
        )}
      </FormContainer>
    )
  })
)

const TodistusLanguageSelection: React.FC<
  React.RefAttributes<HTMLInputElement>
> = observer(
  forwardRef(function TodistusLanguageSelection(_, ref) {
    const { t } = useTranslation()
    const {
      common: { lang },
      steps: {
        [CertificateTypes.UAETTODISTUS]: {
          showStepValidationErrors: showErrors,
        },
      },
    } = useStore()
    const form = useUaetForm()
    const todistusLanguageValue = finalEditableFieldValue(form.todistusLanguage)
    const { setTodistusLanguage } = useUaetFormActions()

    const isTranslationFormAvailable = isCountryInEUByCode(
      finalEditableFieldValue(form.placeOfMarriage)
    )

    const { status, statusText } = getErrorStatus({
      field: 'todistusLanguage',
      errors: getErrors(form),
      showErrors,
      t,
    })

    return (
      <>
        <Heading variant="h3" mb="l">
          <LocalizedString stringKey="orderWizardAdditonalSelectionsStepLanguageSelectionTitle" />
        </Heading>
        <Block mb="m">
          {isTranslationFormAvailable && (
            <Paragraph>
              <LocalizedString stringKey="orderWizardAdditonalSelectionsStepLanguageSelectionText" />
            </Paragraph>
          )}
        </Block>
        <RadioButtonGroup
          id="todistusLanguage"
          data-test-id="todistusLanguage"
          name="todistusLanguage"
          labelText={
            <LocalizedString stringKey="orderWizardAdditonalSelectionsStepLanguageSelectionTitle" />
          }
          labelMode="hidden"
          value={todistusLanguageValue ?? undefined}
          onChange={setTodistusLanguage}
        >
          {availableUAETTodistusLanguages.map((selectableLanguage, index) => (
            <RadioButton
              key={selectableLanguage}
              value={selectableLanguage}
              ref={index === 0 ? ref : undefined}
            >
              <Text>
                {translateLanguage({ code: selectableLanguage, lang })}
              </Text>
            </RadioButton>
          ))}
        </RadioButtonGroup>
        {status === 'error' ? (
          <StatusText ariaLiveMode="polite" status="error">
            {statusText}
          </StatusText>
        ) : null}
      </>
    )
  })
)

const TranslationFormSelections: React.FC<
  React.RefAttributes<HTMLInputElement>
> = observer(
  forwardRef(function TranslationLanguageSelection(_, ref) {
    const { t } = useTranslation()
    const {
      common: { lang },
      steps: {
        [CertificateTypes.UAETTODISTUS]: { showStepValidationErrors },
      },
    } = useStore()
    const form = useUaetForm()

    const translationFormLanguageValue = finalEditableFieldValue(
      form.translationFormLanguage
    )

    const { setIncludeTranslationForm, setTranslationFormLanguage } =
      useUaetFormActions()

    const languageItems = useMemo(
      () =>
        getTranslationFormLanguageSingleSelectData({
          lang,
          suggestLanguageByCountryCode: finalEditableFieldValue(
            form.placeOfMarriage
          ),
        }),
      [lang, form.placeOfMarriage]
    )

    const { status, statusText } = getErrorStatus({
      field: 'translationFormLanguage',
      errors: getErrors(form),
      showErrors: showStepValidationErrors,
      t,
    })

    return (
      <>
        <Heading variant="h3">
          <LocalizedString stringKey="euTranslationFormHeading" />
        </Heading>
        <Paragraph mb="s">
          <LocalizedString stringKey="uaetEuTranslationFormSelectionText" />
        </Paragraph>
        <CheckboxGroup
          labelText={
            <LocalizedString stringKey="orderWizardAdditonalSelectionsStepTranslationSelectionCheckboxTitle" />
          }
          optionalText={t('inputOptional')}
          mb="s"
        >
          {[
            <Checkbox
              key="todistus-translation-form-checkbox"
              checked={
                finalEditableFieldValue(form.includeTranslationForm) ?? false
              }
              onClick={setIncludeTranslationForm}
              data-test-id="todistus-translation-form-checkbox"
            >
              <Text>
                <LocalizedString stringKey="orderWizardAdditonalSelectionsStepTranslationSelectionCheckboxLabel" />
              </Text>
            </Checkbox>,
          ]}
        </CheckboxGroup>

        <SingleSelect
          id="select-translation-language-widget"
          key={`translation-form-${lang}`}
          data-test-id="todistus-translation-form-dropdown"
          labelText={
            <LocalizedString stringKey="orderWizardAdditonalSelectionsStepTranslationSelectionLanguageSelectLabel" />
          }
          visualPlaceholder={t(
            'orderWizardAdditonalSelectionsStepTranslationSelectionLanguageSelectLabel'
          )}
          clearButtonLabel={t('orderWizardSingleSelectClearSelection')}
          noItemsText={t('orderWizardSingleSelectNoMatchingItems')}
          ariaOptionsAvailableTextFunction={(count) =>
            t('orderWizardSingleSelectFilteredResultsAvailable', {
              count,
            })
          }
          disabled={!finalEditableFieldValue(form.includeTranslationForm)}
          items={languageItems}
          selectedItem={
            translationFormLanguageValue
              ? getSingleSelectLanguageDataMapper(lang)(
                  translationFormLanguageValue
                )
              : null
          }
          onItemSelectionChange={setTranslationFormLanguage}
          status={showStepValidationErrors ? status : 'default'}
          statusText={
            showStepValidationErrors && status === 'error'
              ? statusText
              : undefined
          }
          ref={ref}
        />
      </>
    )
  })
)

const ApostilleSelection: React.FC = observer(() => {
  const { t } = useTranslation()
  const form = useUaetForm()
  const { setIncludeApostille } = useUaetFormActions()

  return (
    <>
      <Heading variant="h3">
        <LocalizedString stringKey="orderWizardAdditonalSelectionsStepApostilleSelectionTitle" />
      </Heading>
      <Paragraph>
        <LocalizedString stringKey="orderWizardUaetAdditionalSelectionsStepApostilleSelectionText" />
      </Paragraph>
      <Paragraph mb="s">
        <ExternalLink
          href={t(
            'orderWizardAdditonalSelectionsStepApostilleSelectionTextLinkHref'
          )}
          labelNewWindow={t('opensInNewWindow')}
          underline="initial"
        >
          <LocalizedString stringKey="orderWizardAdditonalSelectionsStepApostilleSelectionLinkText" />
        </ExternalLink>
      </Paragraph>
      <CheckboxGroup
        labelText={
          <LocalizedString stringKey="orderWizardUaetAdditionalSelectionsStepApostilleSelectionCheckboxTitle" />
        }
        optionalText={t('inputOptional')}
      >
        {[
          <Checkbox
            key="todistus-apostille-form-checkbox"
            checked={finalEditableFieldValue(form.includeApostille) ?? false}
            onClick={setIncludeApostille}
            data-test-id="todistus-apostille-form-checkbox"
          >
            <Text>
              <LocalizedString stringKey="orderWizardAdditonalSelectionsStepApostilleSelectionCheckboxLabel" />
            </Text>
          </Checkbox>,
        ]}
      </CheckboxGroup>
    </>
  )
})

function getErrors(form: UaetUiForm) {
  return {
    todistusLanguage: validateUaetTodistusLanguage(
      finalEditableFieldValue(form.todistusLanguage)
    ),
    translationFormLanguage: validateTranslationFormLanguage(
      finalEditableFieldValue(form.translationFormLanguage),
      form
    ),
  }
}

export default UaetAdditionalSelectionStep
